import React, { useState, useEffect, useRef } from "react";
import { Link as RouterLink, useLocation } from "react-router-dom";
import Box from "@mui/material/Box";
import Paper from "@mui/material/Paper";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import Alert from "@mui/material/Alert";
import Avatar from "@mui/material/Avatar";
import FormControl from "@mui/material/FormControl";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import CircularProgress from "@mui/material/CircularProgress";
import LockResetOutlined from "@mui/icons-material/LockResetOutlined";
import { useTheme } from "@mui/material/styles";
import { useBrand } from "../../context/BrandContext";
import MarketingMeasurementScripts from "../../components/MarketingMeasurementScripts";
import { readStoredLocale, setUiLocale, getGlobalLocalesForUiSelect } from "../../i18n/config";
import { parseUiLocaleFromSearch } from "../../i18n/urlLocale";
import { getLoginUiStrings, getLoginLocaleLabel } from "../../i18n/loginUiStrings";
import { apiClient } from "../../api/client";

const COMPANY_RE = /^[a-zA-Z0-9._-]{2,32}$/;
const toSlug = (v) => String(v || "").trim().toLowerCase();

function readLoginLocaleInitial() {
  if (typeof window !== "undefined") {
    const fromUrl = parseUiLocaleFromSearch(window.location.search);
    if (fromUrl) return fromUrl;
  }
  return readStoredLocale() || "pt-BR";
}

export default function ForgotPasswordPage() {
  const brand = useBrand();
  const theme = useTheme();
  const location = useLocation();
  const scrollContainerRef = useRef(null);

  const [email, setEmail] = useState("");
  const [company, setCompany] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [ok, setOk] = useState(false);
  const [localeChoice, setLocaleChoice] = useState(readLoginLocaleInitial);
  const [footerLocaleOpen, setFooterLocaleOpen] = useState(false);

  const L = getLoginUiStrings(localeChoice);

  useEffect(() => {
    const st = location.state;
    if (!st || typeof st !== "object") return;
    const em = typeof st.prefilledEmail === "string" ? st.prefilledEmail.trim() : "";
    const co = typeof st.prefilledCompany === "string" ? st.prefilledCompany.trim() : "";
    if (em) setEmail(em);
    if (co && COMPANY_RE.test(toSlug(co))) setCompany(toSlug(co));
  }, [location.state]);

  useEffect(() => {
    const code = parseUiLocaleFromSearch(location.search);
    if (code) setLocaleChoice(code);
  }, [location.search]);

  useEffect(() => {
    window.scrollTo(0, 0);
    const el = scrollContainerRef.current;
    if (el) el.scrollTop = 0;
  }, []);

  const handleLocaleChoice = (next) => {
    const locale = String(next || "").trim() || "pt-BR";
    setLocaleChoice(locale);
    setUiLocale(locale);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const em = String(email || "").trim().toLowerCase();
    if (!em || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(em)) {
      setError(L.invalidEmail);
      return;
    }
    setError(null);
    setLoading(true);
    try {
      const trimmed = String(company || "").trim();
      const hasWs = trimmed && COMPANY_RE.test(trimmed);
      if (hasWs) {
        const slug = toSlug(trimmed);
        try {
          localStorage.setItem("company", slug);
          localStorage.setItem("xproWorkspaceDatabase", slug);
        } catch {
          /* ignore */
        }
      } else {
        try {
          localStorage.removeItem("company");
        } catch {
          /* ignore */
        }
      }
      await apiClient.post(
        "/users/send-reset",
        { email: em },
        {
          headers: { "Content-Type": "application/json" },
          skipCompanyHeader: !hasWs,
        }
      );
      setOk(true);
    } catch (err) {
      setError(
        err?.response?.data?.message ||
          err?.message ||
          "Não foi possível enviar. Tente novamente."
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      ref={scrollContainerRef}
      display="flex"
      flexDirection="column"
      alignItems="center"
      sx={{
        height: "100vh",
        minHeight: "100vh",
        maxHeight: "100vh",
        background:
          theme.palette.mode === "dark"
            ? "linear-gradient(135deg, #121212, #1e1e1e)"
            : "linear-gradient(135deg, #f5f5f5, #e3e3e3)",
        color: theme.palette.text.primary,
        p: 2,
        pt: 4,
        pb: 4,
        overflowY: "auto",
        boxSizing: "border-box",
      }}
    >
      <MarketingMeasurementScripts />
      <Box display="flex" alignItems="center" justifyContent="center" mb={4} flexShrink={0}>
        <img
          src={theme.palette.mode === "dark" ? brand.assets.logo.dark : brand.assets.logo.light}
          alt={brand.name}
          style={{ height: 60 }}
          loading="lazy"
        />
      </Box>

      <Paper
        elevation={6}
        sx={{
          p: 4,
          maxWidth: 420,
          width: "90%",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          borderRadius: 3,
          bgcolor: theme.palette.background.paper,
        }}
      >
        <Avatar sx={{ bgcolor: theme.palette.primary.main, mb: 2 }}>
          <LockResetOutlined sx={{ color: theme.palette.primary.contrastText }} />
        </Avatar>

        <Typography variant="h5" gutterBottom align="center">
          {L.forgotPasswordTitle}
        </Typography>

        {!ok ? (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2, width: "100%" }}>
            {L.forgotPasswordIntro}
          </Typography>
        ) : null}

        {error && (
          <Alert severity="error" sx={{ width: "100%", mb: 2 }} role="alert">
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
              {L.resetErrorTitle}
            </Typography>
            <Typography variant="body2">{error}</Typography>
          </Alert>
        )}

        {ok ? (
          <>
            <Alert severity="success" sx={{ width: "100%", mb: 2 }}>
              {L.resetSuccess}
            </Alert>
            <Button
              component={RouterLink}
              to="/login"
              fullWidth
              variant="contained"
              sx={{
                height: 48,
                fontWeight: "bold",
                fontSize: "1rem",
                textTransform: "none",
              }}
            >
              {L.signupContinueToLogin}
            </Button>
          </>
        ) : (
          <Box component="form" onSubmit={handleSubmit} width="100%" display="flex" flexDirection="column" gap={1.25}>
            <TextField
              label={L.email}
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              fullWidth
              margin="dense"
              autoComplete="email"
            />
            <TextField
              label={L.workspaceField}
              value={company}
              onChange={(e) => setCompany(toSlug(e.target.value))}
              fullWidth
              margin="dense"
              helperText={L.signupWorkspaceHint}
            />
            <Button
              type="submit"
              variant="contained"
              color="primary"
              disabled={loading}
              fullWidth
              sx={{
                mt: 0.75,
                height: 48,
                fontWeight: "bold",
                fontSize: "1rem",
                textTransform: "none",
              }}
              startIcon={loading && <CircularProgress size={20} color="inherit" />}
            >
              {loading ? L.sending : L.sendLink}
            </Button>
            <Button
              component={RouterLink}
              to="/login"
              fullWidth
              variant="text"
              sx={{ textTransform: "none" }}
            >
              {L.signupContinueToLogin}
            </Button>
          </Box>
        )}
      </Paper>

      <Box
        mt={4}
        pb={2}
        display="flex"
        flexDirection="column"
        alignItems="center"
        gap={0.75}
        sx={{ flexShrink: 0 }}
      >
        {footerLocaleOpen ? (
          <FormControl size="small" variant="standard" sx={{ minWidth: 200 }}>
            <Select
              open={footerLocaleOpen}
              onClose={() => setFooterLocaleOpen(false)}
              value={localeChoice}
              onChange={(e) => {
                handleLocaleChoice(e.target.value);
                setFooterLocaleOpen(false);
              }}
              autoFocus
              MenuProps={{
                anchorOrigin: { vertical: "top", horizontal: "center" },
                transformOrigin: { vertical: "bottom", horizontal: "center" },
              }}
              sx={{
                fontSize: "0.8125rem",
                color: theme.palette.text.secondary,
                "&::before, &::after": { display: "none" },
                "& .MuiSelect-select": { py: 0.25, pr: "24px !important" },
              }}
            >
              {getGlobalLocalesForUiSelect().map(({ code, nativeName }) => (
                <MenuItem key={code} value={code}>
                  {nativeName}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        ) : (
          <Typography
            component="button"
            type="button"
            variant="caption"
            onClick={() => setFooterLocaleOpen(true)}
            aria-label={L.localeLabel}
            aria-haspopup="listbox"
            sx={{
              cursor: "pointer",
              color: theme.palette.text.secondary,
              border: 0,
              background: "none",
              font: "inherit",
              p: 0,
              textDecoration: "underline",
              textDecorationColor: theme.palette.action.disabled,
              textUnderlineOffset: 3,
              "&:hover": {
                color: theme.palette.primary.main,
                textDecorationColor: theme.palette.primary.main,
              },
            }}
          >
            {getLoginLocaleLabel(localeChoice)}
          </Typography>
        )}
        <Typography variant="body2" sx={{ color: theme.palette.text.secondary, textAlign: "center" }}>
          © {new Date().getFullYear()} {brand.name}. Todos os direitos reservados.
        </Typography>
      </Box>
    </Box>
  );
}
